// PDF del Contrato de Servicios Profesionales — 12 cláusulas por tipo
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer'
import {
  generarClausulas,
  generarEncabezamiento,
  getTipoContratoLabel,
  type Clausula,
  type DatosContrato,
} from './contrato-textos'
import { formatDateLong as formatDate } from '@/lib/utils'

// Brand colors
const COLORS = {
  primary: '#ea7e34',
  primaryDark: '#c96a2c',
  dark: '#0f172a',
  darkSecondary: '#1e293b',
  gray: '#64748b',
  grayLight: '#94a3b8',
  border: '#e2e8f0',
  background: '#f8fafc',
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 60,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: COLORS.primary,
  },
  headerLeft: {
    flex: 1,
  },
  logo: {
    maxWidth: 150,
    maxHeight: 50,
  },
  estudioNombre: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.dark,
  },
  estudioSubtitulo: {
    fontSize: 8,
    color: COLORS.gray,
    marginTop: 2,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  contratoLabel: {
    fontSize: 7,
    color: COLORS.grayLight,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  contratoTitulo: {
    fontSize: 12,
    fontWeight: 'bold',
    color: COLORS.primary,
    marginTop: 2,
  },
  fecha: {
    fontSize: 8,
    color: COLORS.gray,
    marginTop: 2,
  },
  // Main title
  tituloSeccion: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.dark,
    textAlign: 'center',
    marginBottom: 6,
  },
  subtituloSeccion: {
    fontSize: 10,
    color: COLORS.gray,
    textAlign: 'center',
    marginBottom: 18,
  },
  // Encabezamiento (intro text)
  encabezamiento: {
    fontSize: 9,
    color: COLORS.darkSecondary,
    lineHeight: 1.5,
    textAlign: 'justify',
    marginBottom: 16,
  },
  // Cláusulas
  clausulaContainer: {
    marginBottom: 12,
  },
  clausulaTitulo: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COLORS.dark,
    marginBottom: 4,
  },
  clausulaNumero: {
    color: COLORS.primary,
  },
  clausulaTexto: {
    fontSize: 9,
    color: COLORS.darkSecondary,
    lineHeight: 1.5,
    textAlign: 'justify',
    marginBottom: 3,
  },
  itemsLista: {
    marginLeft: 12,
    marginTop: 3,
    marginBottom: 3,
  },
  itemRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  itemBullet: {
    width: 10,
    fontSize: 9,
    color: COLORS.primary,
  },
  itemTexto: {
    flex: 1,
    fontSize: 9,
    color: COLORS.darkSecondary,
    lineHeight: 1.4,
  },
  itemsExtraTitulo: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COLORS.dark,
    marginTop: 4,
    marginBottom: 3,
  },
  // Firmas
  firmasContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 40,
    paddingTop: 15,
  },
  firmaBox: {
    width: 200,
    alignItems: 'center',
  },
  firmaImagenes: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 50,
    marginBottom: 4,
  },
  firmaImagen: {
    maxHeight: 50,
    maxWidth: 85,
    marginHorizontal: 4,
  },
  firmaLinea: {
    width: '100%',
    borderTopWidth: 1,
    borderTopColor: COLORS.dark,
    marginBottom: 6,
  },
  firmaLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COLORS.dark,
    textAlign: 'center',
  },
  firmaSubLabel: {
    fontSize: 8,
    color: COLORS.gray,
    textAlign: 'center',
    marginTop: 2,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: COLORS.border,
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerTexto: {
    fontSize: 7,
    color: COLORS.grayLight,
  },
})

export type DatosContratoPDF = DatosContrato

interface ContratoPDFProps {
  datos: DatosContrato
  estudioLogo?: string | null
  firmaUrl?: string | null
  selloUrl?: string | null
}

function ClausulaContenido({ clausula }: { clausula: Clausula }) {
  return (
    <>
      <Text style={styles.clausulaTitulo}>
        <Text style={styles.clausulaNumero}>{clausula.numero}: </Text>
        {clausula.titulo}
      </Text>
      {clausula.parrafos.map((parrafo, i) => (
        <Text key={i} style={styles.clausulaTexto}>
          {parrafo}
        </Text>
      ))}
      {clausula.items && clausula.items.length > 0 && (
        <View style={styles.itemsLista}>
          {clausula.items.map((item, i) => (
            <View key={i} style={styles.itemRow}>
              <Text style={styles.itemBullet}>•</Text>
              <Text style={styles.itemTexto}>{item}</Text>
            </View>
          ))}
        </View>
      )}
      {clausula.itemsExtra && (
        <View>
          {clausula.itemsExtra.titulo ? (
            <>
              <Text style={styles.itemsExtraTitulo}>{clausula.itemsExtra.titulo}</Text>
              <View style={styles.itemsLista}>
                {clausula.itemsExtra.items.map((item, i) => (
                  <View key={i} style={styles.itemRow}>
                    <Text style={styles.itemBullet}>•</Text>
                    <Text style={styles.itemTexto}>{item}</Text>
                  </View>
                ))}
              </View>
            </>
          ) : (
            <>
              {clausula.itemsExtra.items.map((item, i) => (
                <Text key={i} style={styles.clausulaTexto}>
                  {item}
                </Text>
              ))}
            </>
          )}
        </View>
      )}
    </>
  )
}

export function ContratoPDF({ datos, estudioLogo, firmaUrl, selloUrl }: ContratoPDFProps) {
  const clausulas = generarClausulas(datos)
  const ultimaClausula = clausulas[clausulas.length - 1]
  const encabezamiento = generarEncabezamiento(datos)
  const fecha = new Date()
  const hayImagenesDeFirma = Boolean(firmaUrl || selloUrl)

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Header */}
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            {estudioLogo ? (
              <Image src={estudioLogo} style={styles.logo} />
            ) : (
              <View>
                <Text style={styles.estudioNombre}>{datos.estudioNombre}</Text>
                <Text style={styles.estudioSubtitulo}>Servicios Contables Profesionales</Text>
              </View>
            )}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.contratoLabel}>Documento</Text>
            <Text style={styles.contratoTitulo}>Convenio de Servicios</Text>
            <Text style={styles.fecha}>{formatDate(fecha)}</Text>
          </View>
        </View>

        {/* Main title */}
        <Text style={styles.tituloSeccion}>CONVENIO DE TRABAJO POR SERVICIOS PROFESIONALES</Text>
        <Text style={styles.subtituloSeccion}>{getTipoContratoLabel(datos.tipo)}</Text>

        {/* Encabezamiento */}
        <Text style={styles.encabezamiento}>{encabezamiento}</Text>

        {/* Cláusulas. La última se renderiza aparte, junto con las firmas: ver más abajo. */}
        {clausulas.slice(0, -1).map((clausula) => (
          <View key={clausula.numero} style={styles.clausulaContainer} wrap={false}>
            <ClausulaContenido clausula={clausula} />
          </View>
        ))}

        {/* Última cláusula + firmas, en un solo bloque que no se parte.
            Si no entran, saltan JUNTAS: así la última hoja nunca queda con dos líneas de firma
            flotando y el resto en blanco, que es lo que Lisandro reportó como "se desfasó la
            última hoja" (ORR-309). */}
        <View wrap={false}>
          {ultimaClausula && (
            <View style={styles.clausulaContainer}>
              <ClausulaContenido clausula={ultimaClausula} />
            </View>
          )}

          <View style={styles.firmasContainer}>
            <View style={styles.firmaBox}>
              {hayImagenesDeFirma && (
                <View style={styles.firmaImagenes}>
                  {firmaUrl ? <Image src={firmaUrl} style={styles.firmaImagen} /> : null}
                  {selloUrl ? <Image src={selloUrl} style={styles.firmaImagen} /> : null}
                </View>
              )}
              <View style={styles.firmaLinea} />
              <Text style={styles.firmaLabel}>EL PROFESIONAL</Text>
              <Text style={styles.firmaSubLabel}>{datos.estudioResponsable}</Text>
              {datos.estudioMatricula && (
                <Text style={styles.firmaSubLabel}>Matrícula: {datos.estudioMatricula}</Text>
              )}
            </View>
            <View style={styles.firmaBox}>
              {/* El mismo hueco que ocupan firma y sello del profesional. Sin esto la línea del
                  cliente queda ~54pt más arriba que la del profesional: las dos cajas arrancan
                  arriba, pero solo una tiene imágenes empujándola. */}
              {hayImagenesDeFirma && <View style={styles.firmaImagenes} />}
              <View style={styles.firmaLinea} />
              <Text style={styles.firmaLabel}>EL CLIENTE</Text>
              <Text style={styles.firmaSubLabel}>{datos.clienteNombre}</Text>
              {datos.representanteNombre && (
                <Text style={styles.firmaSubLabel}>Representado por: {datos.representanteNombre}</Text>
              )}
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerTexto}>
            {datos.estudioNombre} · {datos.estudioDireccion}
          </Text>
          <Text
            style={styles.footerTexto}
            render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  )
}

export default ContratoPDF
